"use client";
import { Track } from "@/src/lib/tracking";

import { useSnapshot } from "valtio";
import { useOspLogin } from "@features/auth/compat/useOspLogin";
import { useAuth } from "@features/auth";
import { authCacheManager } from "@features/auth/compat/auth-cache-compat";
import type { Web3LoginSupportedType } from "@features/auth/compat/sdk-types";
import { LoginStore } from "@store/modules/login";
import { rootStore } from "@store/rootStore";
import { useMemoizedFn } from "ahooks";
import { pubSubKeyMap } from "@utils/pubSubKeyMap";
import { EmailLoginModal } from "@components/EmailLogin";
import { useEffect, useRef } from "react";
import { postMessageWithResponse } from "../utils/postMessageHelper";
import { pubsub } from "utils/src/pubsub";
import { setEdgenOnboardingStepCookie } from "@utils/onboarding";

function isOwnerInfoPersisted(ownerId?: string | null): boolean {
  if (!ownerId) {
    return false;
  }
  return Boolean(authCacheManager.getOwnerInfoCache(ownerId)?.ownerInfo);
}

export default function LoginPopup() {
  const { login } = useOspLogin();
  const { isLoading: authIsLoading } = useAuth();
  const { isSocialLoginIntent, isLoggedIn } = useSnapshot(LoginStore);
  const { profileState, ownerInfo } = useSnapshot(rootStore.user);
  const parentAuthReadyNotifiedRef = useRef(false);
  const loginController = async (type: Web3LoginSupportedType) => {
    LoginStore.isSocialLoginIntent = true;
    try {
      await login(type);
    } finally {
      LoginStore.isSocialLoginIntent = false;
    }
  };
  useEffect(() => {
    // 接收来自父页面的消息
    window.addEventListener("message", (event: MessageEvent) => {
      Track.log("info", "Login_Popup", "LoginPopup-收到来自父页面的消息", {
        cause: String(event.data),
      });
    });
    return () => {
      window.removeEventListener("message", (event: MessageEvent) => {
        Track.log("info", "Login_Popup", "LoginPopup-移除消息监听", {
          cause: String(event.data),
        });
      });
    };
  }, []);
  useEffect(() => {
    if (!isLoggedIn) {
      parentAuthReadyNotifiedRef.current = false;
      void postMessageWithResponse(window.parent, {
        key: "EdgenMessage",
        payload: { isLoggedIn: false },
      });
      return;
    }

    if (profileState !== "hasProfile" || !ownerInfo?.id) {
      return;
    }

    if (!isOwnerInfoPersisted(ownerInfo.id)) {
      Track.log(
        "info",
        "Login_Popup",
        `LoginPopup-defer parent postMessage until OWNER_INFO_CACHE ownerId=${ownerInfo.id}`,
      );
      return;
    }

    if (parentAuthReadyNotifiedRef.current) {
      return;
    }

    parentAuthReadyNotifiedRef.current = true;
    Track.log(
      "info",
      "Login_Popup",
      `LoginPopup-notify parent login auth ready ownerId=${ownerInfo.id}`,
    );
    void postMessageWithResponse(window.parent, {
      key: "EdgenMessage",
      payload: { isLoggedIn: true, authReady: true },
    });
  }, [isLoggedIn, profileState, ownerInfo?.id]);

  const handleLoginSuccess = useMemoizedFn(async () => {
    try {
      LoginStore.isSocialLoginIntent = false;
      setEdgenOnboardingStepCookie(1);

      pubsub.emit(pubSubKeyMap.LOGIN_SUCCESS);
    } catch (e) {}
  });
  useEffect(() => {
    if (isLoggedIn) {
      // 登录成功后直接处理
      handleLoginSuccess();
    }
  }, [isLoggedIn, handleLoginSuccess]);

  return (
    <EmailLoginModal
      open={true}
      onClose={() => {}}
      onSocialClick={loginController}
      loading={authIsLoading || !!isSocialLoginIntent}
      isPopup
    />
  );
}
